import { GWElement } from 'lib/elements'
import { sleep, transitionComplete } from 'utils/gw'
const ATTRIBUTE_KEYS = ['email', 'phoneNumber', 'firstName', 'lastName']
const PROFILE_KEYS = ['firstName', 'lastName', 'birthday', 'frequency']
const PROFILE_IDENTIFIER_KEYS = ['id', 'email', 'phoneNumber']

export class EmailPreferencesForm extends GWElement {
  constructor() {
    super()

    this.overlay = this.querySelector('[data-email-preferences-form-overlay]')
    this.getProfileFromParams()

    this.form = this.querySelector('form')
    this.submitButton = this.querySelector('button[type="submit"]')
    this.inputs = [...this.form.querySelectorAll('[data-input]')].reduce((acc, input) => {
      acc[input.name] = input
      return acc
    }, {})

    this.dateInputs = [...this.form.querySelectorAll('[data-input][type="date"]')]

    this.addListener(this.form, 'input', this.onFormChange)
    this.addListener(this.form, 'submit', this.onSubmit)
    this.dateInputs.forEach(input => {
      this.onDateChange(input)
      this.addListener(input, 'change', e => this.onDateChange(input))
    })
  }

  get optionDefaults() {
    return {
      state: 'idle',
    }
  }

  static get observedAttributes() {
    return ['state']
  }

  waitForButtonTransition(callback) {
    this.buttonStatesWrapper.addEventListener('transitionend', callback, { once: true })
  }

  async onFormChange(event) {
    if (this.state === 'submitting') {
      event.preventDefault()
      return
    }

    if (this.state !== 'idle') {
      this.submitButton.classList.add('is-reverting')
      await transitionComplete(this.buttonStatesWrapper)
      this.state = 'idle'
      // TODO: Add utility function `nextTick` to use await instead (await sleep(0))
      setTimeout(() => this.submitButton.classList.remove('is-reverting'))
    }
  }

  onDateChange(input) {
    const hasValue = input.value.length > 0
    input.classList.toggle('has-value', hasValue)
  }

  getProfileFromSource(source, propertyCallback) {
    this.profile = [...PROFILE_KEYS, ...PROFILE_IDENTIFIER_KEYS].reduce((profile, key) => {
      const value = propertyCallback ? source[propertyCallback](key) : source[key]

      if (!(key in profile) || (value !== null && typeof value !== 'undefined')) {
        profile[key] = value
      }

      return profile
    }, this.profile ?? {})

    this.profile.subscribed = source.subscribed ?? true

    // if (!(source instanceof URLSearchParams)) {
    //   for (const [key, value] of Object.entries(this.profile)) {
    //     if (value === null || typeof value === 'undefined') continue
    //     this.params.set(key, value)
    //   }

    //   window.history.replaceState({}, '', `?${this.params.toString()}`)
    // }

    return this.profile
  }

  async showOverlay() {
    this.overlay.style.opacity = 0
    this.overlay.style.display = 'flex'
    await this.overlay.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: 400,
      easing: 'cubic-bezier(0.645, 0.045, 0.355, 1)',
      fill: 'both',
    }).finished
  }

  async hideOverlay() {
    await this.overlay.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: 400,
      easing: 'cubic-bezier(0.645, 0.045, 0.355, 1)',
      fill: 'both',
    }).finished
    this.overlay.style.display = 'none'
  }

  async getProfileFromId(id) {
    if (!id) return false

    try {
      const shop = Shopify.shop.replace('.myshopify.com', '')
      const url = `/tools/api/klaviyo/profile/${id}?shop=${shop}`
      const response = await fetch(url, { headers: { 'Content-Type': 'application/json' } })
      const { profile } = await response.json()
      this.getProfileFromSource(profile)
    } catch (error) {
      console.error('Error getting profile from ID', error)
    }
  }

  async getProfileFromParams() {
    // this.showOverlay()
    this.params ??= new URLSearchParams(window.location.search)

    if (!this.params.has('email') && !this.params.has('phoneNumber')) {
      const id = this.params.get('id')

      await this.getProfileFromId(id)
    } else {
      this.getProfileFromSource(this.params, 'get')
    }

    // Redirect to homepage if no profile found
    if (!this.profile && !Shopify.designMode) return this.noProfile()
    document.body.classList.add('email-preferences-form-active')

    this.populateInputs()

    await sleep(0)
    document.dispatchEvent(new CustomEvent('klaviyo:profile-loaded'))
    this.hideOverlay()

    return this.profile
  }

  // TODO: Account for loading state / mid-submit share
  shareProfile() {
    if (!this.profile) {
      return new Promise(resolve => {
        document.addEventListener('klaviyo:profile-loaded', () => resolve(this.profile), { once: true })
      })
    }

    return this.profile
  }

  noProfile() {
    window.location.href = '/'
  }

  async init() {
    super.init()

    this.initSubmitButton()

    if (this.profile) {
      this.populateInputs()
    }
  }

  getStateMarkup(state, text) {
    return `<span class="button__state" data-state="${state}">${text}</span>`
  }

  // TODO: Add localization
  initSubmitButton() {
    const buttonContent = `
      <span class="button__state button__state--placeholder">
        ${this.submitButton.textContent}
      </span>
      <span class="button__states">
        ${this.getStateMarkup('idle', this.submitButton.textContent)}
        ${this.getStateMarkup('error', 'Error Saving Preferences')}
        ${this.getStateMarkup('submitting', 'Saving Preferences...')}
        ${this.getStateMarkup('success', 'Preferences Saved!')}
      </span>
    `

    this.submitButton.innerHTML = buttonContent
    this.submitButton.classList.add('button--dynamic-submit')
    this.buttonStatesWrapper = this.submitButton.querySelector('.button__states')
  }

  // TODO: Support multiple values for checkbox fieldset
  populateInputs() {
    Object.entries(this.inputs).forEach(([name, input]) => {
      if (this.profile) {
        if (input.localName === 'fieldset') {
          const inputTarget = input.querySelector(`input[value="${this.profile[name]}"]`)
          if (inputTarget) inputTarget.checked = true

          return
        }

        input.value = this.profile[name]
      }

      if (input.type === 'date') {
        this.onDateChange(input)
      }
    })
  }

  // set submitting(value) {
  //   this.submitButton.disabled = value
  // }

  get state() {
    return this.options.state
  }

  set state(value) {
    this.setAttribute('state', value)
    this.submitButton.disabled = value === 'submitting'
    this.submitButton.dataset.state = value
  }

  async onSubmit(event) {
    event.preventDefault()

    if (this.state !== 'idle') return
    let successful
    this.state = 'submitting'
    const startTime = Date.now()

    const formData = new FormData(this.form)
    const properties = Object.fromEntries(formData.entries())

    const identifiers = PROFILE_IDENTIFIER_KEYS.reduce((identifiers, key) => {
      if (this.profile[key]) {
        identifiers[key] = this.profile[key]
      }
      return identifiers
    }, {})

    console.debug('Updating profile', properties)

    try {
      const updatedProfile = await this.updateWithIdentifiers(properties, identifiers)

      this.getProfileFromSource(updatedProfile)

      successful = true
    } catch (error) {
      console.error('Error updating profile', error)
      successful = false
    } finally {
      const endTime = Date.now()
      const elapsedTime = endTime - startTime

      // Ensure submission takes at least 1 second to prevent awkward loading state
      if (elapsedTime < 1000) {
        await sleep(1000 - elapsedTime)
      }
      this.state = successful ? 'success' : 'error'
    }
  }

  async updateWithIdentifiers(_attributes, identifiers) {
    const shop = Shopify.shop.replace('.myshopify.com', '')
    // TODO: rework this to be cleaner
    const attributes = Object.entries(_attributes).reduce(
      (acc, [key, value]) => {
        if (!ATTRIBUTE_KEYS.includes(key)) {
          acc.properties[key] = value
        } else {
          acc[key] = value
        }
        return acc
      },
      { properties: {} }
    )

    const body = { attributes, identifiers, shop }
    const url = '/tools/api/klaviyo/profile'

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      const responseData = await response.json()
      let { profile } = responseData
      profile = { ...profile, ...profile.properties }

      return profile
    } catch (error) {
      console.error('Error updating profile', error)
      throw error
    }
  }
}

if (!customElements.get('email-preferences-form')) {
  customElements.define('email-preferences-form', EmailPreferencesForm)
}
